import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, CurrencyEur, Users, MusicNote, ChartLine, Broadcast, Lightning,
  Calendar, Sparkle
} from "@phosphor-icons/react";

const StatCard = ({ icon: Icon, label, value, accent = "#E11D48", testId }) => (
  <div data-testid={testId} className="p-5 rounded-2xl border border-white/10 bg-[#0f0f10]">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-widest text-zinc-500">{label}</span>
      <Icon size={20} weight="duotone" style={{ color: accent }} />
    </div>
    <div className="font-display font-black text-3xl">{value}</div>
  </div>
);

const ArtistDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [concerts, setConcerts] = useState([]);
  const [fans, setFans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", scheduled_at: "", is_free: true, price_eur: 0,
    cover_image_url: "", genre: "Pop",
  });

  const refresh = async () => {
    const [s, c, f, sub] = await Promise.all([
      api.get("/artist/stats"),
      api.get("/concerts", { params: { artist_id: user?.id } }),
      api.get("/fanbase"),
      api.get("/artist/subscription"),
    ]);
    setStats(s.data); setConcerts(c.data); setFans(f.data); setSubscription(sub.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) refresh(); }, [user]);

  // Detect Stripe subscription return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (params.get("subscription") === "success" && sessionId) {
      api.get(`/checkout/status/${sessionId}`)
        .then(() => { toast.success("Abonnement Pro activé ! 🎉 Concerts illimités."); refresh(); })
        .catch(() => {});
      window.history.replaceState({}, "", "/dashboard");
    } else if (params.get("subscription") === "cancelled") {
      toast.error("Abonnement annulé.");
      window.history.replaceState({}, "", "/dashboard");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/artist/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setSubscribing(false);
    }
  };

  const createConcert = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString(), price_eur: Number(form.price_eur) };
      await api.post("/concerts", payload);
      toast.success("Concert créé !");
      setOpen(false);
      setForm({ title: "", description: "", scheduled_at: "", is_free: true, price_eur: 0, cover_image_url: "", genre: "Pop" });
      refresh();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail);
        setOpen(false);
      } else {
        toast.error(err.response?.data?.detail || "Erreur");
      }
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#E11D48] font-bold mb-2">Tableau de bord</p>
            <h1 className="font-display font-black text-4xl tracking-tight">Bonjour {user.stage_name || user.full_name}</h1>
            <p className="text-zinc-400 mt-2">Pilotez vos concerts, votre revenu et votre fanbase.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="dash-new-concert" className="bg-[#E11D48] hover:bg-[#BE123C]">
                <Plus className="mr-2" weight="bold" /> Nouveau concert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0f0f10] border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Programmer un concert</DialogTitle>
              </DialogHeader>
              <form onSubmit={createConcert} className="space-y-4">
                <div>
                  <Label>Titre</Label>
                  <Input data-testid="form-title" required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="bg-black/40 border-white/10 mt-1" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea data-testid="form-desc" required value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-black/40 border-white/10 mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date & heure</Label>
                    <Input data-testid="form-date" type="datetime-local" required value={form.scheduled_at} onChange={(e) => setForm({...form, scheduled_at: e.target.value})} className="bg-black/40 border-white/10 mt-1" />
                  </div>
                  <div>
                    <Label>Genre</Label>
                    <Input data-testid="form-genre" value={form.genre} onChange={(e) => setForm({...form, genre: e.target.value})} className="bg-black/40 border-white/10 mt-1" />
                  </div>
                </div>
                <div>
                  <Label>URL image de couverture</Label>
                  <Input data-testid="form-cover" placeholder="https://..." value={form.cover_image_url} onChange={(e) => setForm({...form, cover_image_url: e.target.value})} className="bg-black/40 border-white/10 mt-1" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-white/10 bg-black/30">
                  <div>
                    <div className="font-bold text-sm">Concert gratuit</div>
                    <div className="text-xs text-zinc-500">Sinon, fixez votre prix</div>
                  </div>
                  <Switch data-testid="form-is-free" checked={form.is_free} onCheckedChange={(v) => setForm({...form, is_free: v})} />
                </div>
                {!form.is_free && (
                  <div>
                    <Label>Prix du ticket (€)</Label>
                    <Input data-testid="form-price" type="number" step="0.5" min="0.5" required value={form.price_eur} onChange={(e) => setForm({...form, price_eur: e.target.value})} className="bg-black/40 border-white/10 mt-1" />
                  </div>
                )}
                <DialogFooter>
                  <Button data-testid="form-submit" type="submit" className="bg-[#E11D48] hover:bg-[#BE123C] w-full">Programmer</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard testId="stat-concerts" icon={MusicNote} label="Concerts" value={stats?.concerts_count ?? "—"} />
          <StatCard testId="stat-revenue" icon={CurrencyEur} label="Revenus nets" value={stats ? `${stats.total_revenue_eur}€` : "—"} accent="#10B981" />
          <StatCard testId="stat-attendees" icon={Users} label="Spectateurs" value={stats?.total_attendees ?? "—"} />
          <StatCard testId="stat-fans" icon={Sparkle} label="Fans (base)" value={stats?.fans_count ?? "—"} accent="#F59E0B" />
        </div>

        {/* Subscription banner */}
        {subscription && (
          subscription.is_active ? (
            <div data-testid="sub-active" className="mb-10 rounded-2xl border border-[#10B981]/30 bg-gradient-to-r from-[#0a1410] to-[#0f0f10] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#10B981]/15 flex items-center justify-center">
                  <Lightning size={22} weight="fill" className="text-[#10B981]" />
                </div>
                <div>
                  <div className="font-display font-bold">Abonnement Pro actif</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Concerts illimités · Renouvellement le {new Date(subscription.expires_at).toLocaleDateString("fr-FR")}</div>
                </div>
              </div>
              <div className="text-xs font-mono text-[#10B981]">19€ / mois</div>
            </div>
          ) : (
            <div data-testid="sub-banner" className="mb-10 rounded-2xl border border-[#F59E0B]/40 bg-gradient-to-r from-[#1a1206] to-[#0f0f10] p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center">
                  <Lightning size={22} weight="fill" className="text-[#F59E0B]" />
                </div>
                <div>
                  <div className="font-display font-bold">
                    {subscription.trial_left > 0
                      ? `${subscription.trial_left} concert${subscription.trial_left > 1 ? "s" : ""} d'essai restant${subscription.trial_left > 1 ? "s" : ""}`
                      : "Période d'essai terminée"}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    {subscription.trial_left > 0
                      ? `Après vos ${subscription.trial_concerts} concerts gratuits, passez Pro à 19€/mois pour des concerts illimités.`
                      : "Passez à l'abonnement Pro pour continuer à créer des concerts."}
                  </div>
                </div>
              </div>
              <Button data-testid="sub-upgrade" disabled={subscribing} onClick={subscribe} className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-bold">
                {subscribing ? "Redirection..." : "Passer Pro · 19€/mois"}
              </Button>
            </div>
          )
        )}

        {/* Concerts list */}
        <div className="mb-12">
          <h2 className="font-display font-bold text-2xl mb-4">Mes concerts</h2>
          <div className="space-y-3">
            {concerts.length === 0 && <p className="text-zinc-500 text-sm">Aucun concert encore. Créez votre premier !</p>}
            {concerts.map((c) => (
              <div key={c.id} className="p-4 rounded-xl border border-white/10 bg-[#0f0f10] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-4 flex-1">
                  <img src={c.cover_image_url || "https://images.pexels.com/photos/30215324/pexels-photo-30215324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400"} className="w-16 h-16 rounded-lg object-cover" alt="" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {c.is_live && <span className="px-2 py-0.5 rounded bg-red-600 text-xs font-bold">LIVE</span>}
                      <h3 className="font-bold">{c.title}</h3>
                    </div>
                    <div className="text-xs text-zinc-500 flex gap-3 mt-1 font-mono">
                      <span><Calendar size={12} className="inline" /> {new Date(c.scheduled_at).toLocaleString("fr-FR")}</span>
                      <span>{c.is_free ? "Gratuit" : `${c.price_eur}€`}</span>
                      <span>{c.attendees_count} fans</span>
                      <span className="text-[#10B981]">{c.revenue_eur?.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white" asChild>
                    <Link to={`/concert/${c.id}`}>Voir</Link>
                  </Button>
                  <Button size="sm" className="bg-[#E11D48] hover:bg-[#BE123C]" asChild>
                    <Link to={`/live/${c.id}`}><Broadcast className="mr-1" /> Live room</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fanbase */}
        <div>
          <h2 className="font-display font-bold text-2xl mb-4">Ma fanbase ({fans.length})</h2>
          <div className="rounded-xl border border-white/10 bg-[#0f0f10] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-black/40">
                <tr className="text-left text-xs uppercase tracking-widest text-zinc-500">
                  <th className="p-3">Nom</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Concerts</th>
                  <th className="p-3">Dernière vue</th>
                </tr>
              </thead>
              <tbody>
                {fans.map((f) => (
                  <tr key={f.email} className="border-t border-white/5">
                    <td className="p-3">{f.name}</td>
                    <td className="p-3 font-mono text-xs text-zinc-300">{f.email}</td>
                    <td className="p-3">{f.concerts?.length || 0}</td>
                    <td className="p-3 text-zinc-400 text-xs">{new Date(f.last_seen).toLocaleDateString("fr-FR")}</td>
                  </tr>
                ))}
                {fans.length === 0 && (
                  <tr><td colSpan="4" className="p-6 text-center text-zinc-500">Aucun fan encore. Programmez un concert pour démarrer !</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistDashboard;
